'use client'
import { PrismicImage } from "@prismicio/react";
import { isFilled } from "@prismicio/client";
import { useState, useEffect, useRef } from "react";
import { DotLottieReact } from '@lottiefiles/dotlottie-react';


/**
 * @typedef {import("@prismicio/client").Content.HeroSlice} HeroSlice
 * @typedef {import("@prismicio/react").SliceComponentProps<HeroSlice>} HeroProps
 * @type {import("react").FC<HeroProps>}
 */

const NoiseBackground = ({ fillColor = "#ffffff" }) => (
  <svg
    className="absolute w-full h-full pointer-events-none opacity-70"
    width="100%"
    height="100%"
    viewBox="0 0 1440 721"
    preserveAspectRatio="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <defs>
      <filter
        id="noiseFilterMobile"
        x="0"
        y="0"
        width="100%"
        height="100%"
        filterUnits="userSpaceOnUse"
        colorInterpolationFilters="sRGB"
      >
        <feFlood floodOpacity="0" result="BackgroundImageFix" />
        <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />

        <feTurbulence
          type="fractalNoise"
          baseFrequency="5 5"
          stitchTiles="stitch"
          numOctaves="3"
          result="noise"
          seed="1473"
        />

        <feColorMatrix in="noise" type="luminanceToAlpha" result="alphaNoise" />
        <feComponentTransfer in="alphaNoise" result="coloredNoise1">
          <feFuncA
            type="discrete"
            tableValues="1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0"
          />
        </feComponentTransfer>
        <feComposite operator="in" in2="shape" in="coloredNoise1" result="noise1Clipped" />
        <feFlood floodColor="rgba(0, 0, 0, 0.1)" result="color1Flood" />
        <feComposite operator="in" in2="noise1Clipped" in="color1Flood" result="color1" />
        <feMerge result="effect1_noise_mobile">
          <feMergeNode in="shape" />
          <feMergeNode in="color1" />
        </feMerge>
      </filter>

      <filter
        id="noiseFilterDesktop"
        x="0"
        y="0"
        width="100%"
        height="100%"
        filterUnits="userSpaceOnUse"
        colorInterpolationFilters="sRGB"
      >
        <feFlood floodOpacity="0" result="BackgroundImageFix" />
        <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
        <feTurbulence
          type="fractalNoise"
          baseFrequency="50 50"
          stitchTiles="stitch"
          numOctaves="3"
          result="noise"
          seed="1473"
        />

        <feColorMatrix in="noise" type="luminanceToAlpha" result="alphaNoise" />
        <feComponentTransfer in="alphaNoise" result="coloredNoise1">
          <feFuncA
            type="discrete"
            tableValues="1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0"
          />
        </feComponentTransfer>
        <feComposite operator="in" in2="shape" in="coloredNoise1" result="noise1Clipped" />
        <feFlood floodColor="rgba(0, 0, 0, 0.1)" result="color1Flood" />
        <feComposite operator="in" in2="noise1Clipped" in="color1Flood" result="color1" />
        <feMerge result="effect1_noise_desktop">
          <feMergeNode in="shape" />
          <feMergeNode in="color1" />
        </feMerge>
      </filter>
    </defs>

    <g filter="url(#noiseFilterMobile)" className="md:hidden">
      <rect width="100%" height="100%" fill={fillColor} />
    </g>

    <g filter="url(#noiseFilterDesktop)" className="hidden md:block">
      <rect width="100%" height="100%" fill={fillColor} />
    </g>
  </svg>
);


const Hero = ({ slice }) => {
  const noiseColor = slice.primary.bg_color === "light-orange"
    ? "#FCC453"
    : slice.primary.bg_color === "light-green"
      ? "#DFE05B"
      : "#F1AEC0";
  const [isAnimated, setIsAnimated] = useState(false);
  const [showOverlay, setShowOverlay] = useState(true);
  const sectionRef = useRef(null);
  const timeoutRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && window.innerWidth < 1024) {
            timeoutRef.current = setTimeout(() => {
              setIsAnimated(true);
            }, 500);
          } else {
            if (timeoutRef.current) {
              clearTimeout(timeoutRef.current);
            }
            setIsAnimated(false);
          }
        });
      },
      { threshold: 0.5 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  // Fade out overlay after 3 seconds
  useEffect(() => {
    const overlayTimeout = setTimeout(() => {
      setShowOverlay(false);
    }, 3000);

    return () => clearTimeout(overlayTimeout);
  }, []);

  return (
    <section ref={sectionRef} data-slice-type={slice.slice_type} data-slice-variation={slice.variation} data-bg-color={slice.primary.bg_color}
      className={` ${slice.variation === "default" ? "h-screen" : "h-fit"} max-h-150 md:max-h-250 lg:max-h-none`}>
      {slice.variation === "photo" && (
        <div className={`bg-${slice.primary.bg_color} relative z-10 w-full h-full overflow-hidden group`}>
          <NoiseBackground fillColor={noiseColor} />
          {isFilled.image(slice.primary.img) && (
            <div className="hidden lg:block xl:max-w-300 xl:mx-auto h-full">
              <PrismicImage
                field={{
                  ...slice.primary.img,
                  alt: slice.primary.img.alt || "Image d'illustration",
                }}
                className="h-full w-full object-contain relative z-10 object-bottom translate-y-35 group-hover:translate-y-0 transition-transform duration-700"
              />
            </div>
          )}
          {isFilled.image(slice.primary.img_mobile) && (
            <div className="lg:hidden h-full w-full">
              <PrismicImage
                field={{
                  ...slice.primary.img_mobile,
                  alt: slice.primary.img_mobile.alt || "Image d'illustration mobile",
                }}
                className={`${isAnimated ? 'translate-y-0' : 'translate-y-20'} h-full w-full object-contain relative z-10 object-bottom transition-transform duration-700`}
              />
            </div>
          )}

          <div className="absolute w-full h-full absolute-center z-0">
            {isFilled.keyText(slice.primary.text) &&
              <div className="text-center flex flex-col justify-center items-center w-full h-full">
                <h1
                  className={`text-hero-photo whitespace-nowrap ${slice.primary.bg_color === "light-orange"
                    ? `text-light-orange ${isAnimated ? "!text-dark-orange" : ""} lg:!text-light-orange lg:group-hover:!text-dark-orange`
                    : slice.primary.bg_color === "light-green"
                      ? `text-light-green ${isAnimated ? "!text-dark-green" : ""} lg:!text-light-green lg:group-hover:!text-dark-green`
                      : `text-light-pink ${isAnimated ? "!text-dark-pink" : ""} lg:!text-light-pink lg:group-hover:!text-dark-pink`
                    }`}
                >
                  {slice.primary.text}
                  <br />{slice.primary.text}
                  <br />{slice.primary.text}
                </h1>
              </div>
            }
          </div>
        </div>
      )}

      {slice.variation === "default" && (
        <div className="relative w-full h-full">
          {isFilled.link(slice.primary.url_video) && (
            <video
              onSuspend={() => {
                console.log('video suspended');
              }}
              autoPlay loop muted playsInline src={slice.primary.url_video.url} className="hidden md:block w-full h-full object-cover">
              Your browser does not support the video tag.
            </video>
          )}

          {isFilled.link(slice.primary.url_video_mobile) && (
            <video
              onSuspend={() => {
                console.log('video suspended');
              }}
              autoPlay loop muted playsInline src={slice.primary.url_video_mobile.url} className="block md:hidden w-full h-full object-cover">
              Your browser does not support the video tag.
            </video>
          )}

          {/* Beige overlay that fades out after 3 seconds */}
          <div
            className={`absolute inset-0 bg-beige transition-opacity duration-1000 ease-in-out ${showOverlay ? 'opacity-100 z-200' : 'opacity-0 z-100'} pointer-events-none`}
          />
          <div className={`absolute inset-0 w-[90%] md:w-1/2 flex justify-center items-center m-auto ${showOverlay ? 'z-210' : 'z-110'}`}>
            <DotLottieReact
              className="absolute inset-0 top-0 left-0"
              src="https://lottie.host/22f7f507-410e-4944-9131-fac4064ab2c2/mywaNZMVzA.json"
              autoplay
            />
          </div>
        </div>
      )}
    </section>
  );
};

export default Hero;
